import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { diccionarioDe, type Idioma } from '../../i18n/index.js';
import type { FalloDeAthena } from '../services/api.js';
import { CaptchaWidget } from './CaptchaWidget.js';

export const MODOS = ['preguntar', 'contrastar', 'comparar'] as const;
export type Modo = (typeof MODOS)[number];
/** MODO-01: sin modo elegido, lo elige Athena según la pregunta. */
export type ModoDeLaCaja = Modo | 'auto';

/** Los mismos largos que impone el API. Se comprueban aquí para no gastar una llamada en un 400. */
export const LARGO_MINIMO = 10;
export const LARGO_MAXIMO = 600;
export const LARGO_MAXIMO_CONTRASTAR = 2000;

export function largoMaximoDe(modo: ModoDeLaCaja): number {
  return modo === 'contrastar' || modo === 'auto' ? LARGO_MAXIMO_CONTRASTAR : LARGO_MAXIMO;
}

export function sePuedeEnviar(texto: string, modo: ModoDeLaCaja): boolean {
  const limpio = texto.trim();
  return limpio.length >= LARGO_MINIMO && limpio.length <= largoMaximoDe(modo);
}

/**
 * La caja de consulta: un texto y nada más.
 *
 * **No se elige el modo** (MODO-01): Athena decide si es una pregunta, una cifra que contrastar o
 * dos cosas que comparar, y lo dice encima de la respuesta con la opción de cambiarlo. En
 * automático cabe un párrafo entero, porque puede ser una frase de prensa para contrastar.
 *
 * El botón se deshabilita mientras se espera, y eso **no es cosmética**: una consulta cuesta dinero
 * de verdad y el tope de gasto es lo único que mantiene Athena gratis y sin cuenta. Dejar pulsar
 * cinco veces seguidas es dejar que alguien se gaste el tope de la hora sin querer.
 */
export function Caja({
  idioma,
  modo,
  enCurso,
  fallo,
  textoInicial = '',
  placeholder,
  textoEnviar,
  enviarAlMontar = false,
  conCaptcha = true,
  onEnviar,
}: {
  readonly idioma: Idioma;
  readonly modo: ModoDeLaCaja;
  readonly enCurso: boolean;
  readonly fallo: FalloDeAthena | null;
  readonly textoInicial?: string;
  readonly placeholder?: string;
  readonly textoEnviar?: string;
  /** La pregunta escrita en la portada se manda sola al llegar a `/consulta`, sin un segundo clic. */
  readonly enviarAlMontar?: boolean;
  /**
   * `false` en la portada (2026-09-28, Iván lo notó pidiendo dos veces el captcha en su móvil): esa
   * caja nunca manda la pregunta de verdad, solo navega a `/consulta` con el texto; el widget solo
   * tiene sentido donde el `POST` sale de verdad.
   */
  readonly conCaptcha?: boolean;
  readonly onEnviar: (texto: string, captchaToken: string | null) => void;
}) {
  const t = diccionarioDe(idioma).caja;
  const [texto, setTexto] = useState(textoInicial);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  /**
   * **Envío optimista** (2026-09-28, reemplaza el diseño anterior que montaba el widget siempre y
   * esperaba a tenerlo listo antes del primer intento): se manda sin token de entrada; si hay un
   * pase de captcha válido (la cookie, que el frontend no puede leer), el backend lo acepta y el
   * widget nunca llega a montarse. El widget solo aparece tras el primer rechazo real
   * (`captcha_invalido`), y solo si ese rechazo fue sin token: si ya se mandó uno y aun así lo
   * rechazó, es un fallo de verdad y se enseña como tal, no como "falta el captcha".
   */
  const [widgetMontado, setWidgetMontado] = useState(false);
  useEffect(() => {
    if (conCaptcha && fallo?.tipo === 'captcha_invalido') setWidgetMontado(true);
  }, [conCaptcha, fallo]);
  const faltaResolverCaptcha = widgetMontado && fallo?.tipo === 'captcha_invalido' && captchaToken === null;

  const intentarEnviar = (tokenActual: string | null) => {
    if (enCurso || !sePuedeEnviar(texto, modo)) return;
    onEnviar(texto.trim(), tokenActual);
  };

  const enviar = (e: FormEvent | KeyboardEvent) => {
    e.preventDefault();
    intentarEnviar(captchaToken);
  };

  const yaEnviadoAlMontar = useRef(false);
  useEffect(() => {
    if (!enviarAlMontar || yaEnviadoAlMontar.current) return;
    yaEnviadoAlMontar.current = true;
    intentarEnviar(null);
    // Se envía una sola vez, tal cual está el texto al montar: sin esperar a nada, el backend
    // decide si hacía falta captcha (ver el envío optimista, arriba).
  }, []);

  /**
   * **Reintento automático**: en cuanto el widget (montado tras un rechazo) resuelve un token, se
   * reenvía sola, sin que la persona tenga que volver a pulsar. Con `interaction-only` eso pasa en
   * milisegundos casi siempre; dejarlo colgado del segundo clic sería peor experiencia que el
   * propio doble captcha que este cambio arregla.
   */
  const tokenAnterior = useRef<string | null>(null);
  useEffect(() => {
    const eraNulo = tokenAnterior.current === null;
    tokenAnterior.current = captchaToken;
    if (widgetMontado && eraNulo && captchaToken !== null) intentarEnviar(captchaToken);
    // Solo depende del token: reacciona cuando el widget resuelve, no cuando cambia el texto.
  }, [captchaToken]);

  /** Ctrl o Cmd más Intro envía. Intro solo sigue siendo un salto de línea: contrastar pega frases largas. */
  const alPulsarTecla = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) enviar(e);
  };

  return (
    <form onSubmit={enviar} className='flex w-full max-w-[780px] flex-col gap-3'>
      <div className='flex flex-col rounded-xl border border-(--line-strong) bg-(--chat-bg)'>
        <label htmlFor='consulta' className='sr-only'>
          {t.etiqueta}
        </label>
        <textarea
          id='consulta'
          value={texto}
          onChange={e => {
            setTexto(e.target.value);
          }}
          onKeyDown={alPulsarTecla}
          maxLength={largoMaximoDe(modo)}
          rows={modo === 'preguntar' || modo === 'comparar' ? 2 : 3}
          placeholder={placeholder ?? t.placeholder[modo]}
          className='resize-none border-none bg-transparent px-[26px] pt-6 pb-[14px] font-(family-name:--text) text-[19px] text-(--ink) outline-none'
        />
        <div className='flex items-center justify-between px-[26px] pb-4'>
          <span className='font-(family-name:--mono) text-[12px] text-(--ink-faint)'>
            {String(texto.trim().length)} / {String(largoMaximoDe(modo))}
          </span>
          {/*
            El botón de enviar NO puede llamarse igual que un modo («Preguntar»): encima de la
            respuesta hay botones para responder en otro modo, y dos botones con el mismo nombre
            accesible no los distingue un lector de pantalla.
          */}
          <button
            type='submit'
            disabled={enCurso || !sePuedeEnviar(texto, modo)}
            className='sobre-teal cursor-pointer rounded-full bg-(--teal-deep) px-5 py-2 font-(family-name:--text) text-[15px] disabled:cursor-not-allowed disabled:opacity-40'
          >
            {enCurso ? t.enviando : (textoEnviar ?? t.enviar)}
          </button>
        </div>
      </div>

      {widgetMontado ? <CaptchaWidget onCambio={setCaptchaToken} /> : null}

      {faltaResolverCaptcha ? (
        <p role='alert' className='m-0 font-(family-name:--text) text-[14px] text-(--ink-soft)'>
          {t.error.captcha_pendiente}
        </p>
      ) : fallo === null ? null : fallo.tipo === 'tope_de_gasto_anonimo' ? (
        <p role='alert' className='m-0 font-(family-name:--text) text-[14px] text-(--ink-soft)'>
          {t.error.tope_de_gasto_anonimo.antes}
          <Link to='/registro' className='text-(--teal-deep) underline'>
            {t.error.tope_de_gasto_anonimo.enlace}
          </Link>
          {t.error.tope_de_gasto_anonimo.despues}
        </p>
      ) : (
        <p role='alert' className='m-0 font-(family-name:--text) text-[14px] text-(--ink-soft)'>
          {mensajeDelFallo(fallo, idioma)}
        </p>
      )}
    </form>
  );
}

/**
 * Cada código dice una cosa distinta y quien pregunta merece saber cuál. Con `Retry-After` se le
 * dice cuántos segundos, porque "espera un momento" no deja decidir si cerrar la pestaña.
 *
 * `tope_de_gasto_anonimo` lleva un enlace a `/registro` dentro del texto: el propio `Caja`
 * lo pinta con un `<Link>` de verdad y no llama aquí para ese caso. Esta función igual da un texto
 * plano con las tres partes seguidas, para quien solo necesite el mensaje (por ejemplo, un test).
 */
export function mensajeDelFallo(fallo: FalloDeAthena, idioma: Idioma): string {
  const t = diccionarioDe(idioma).caja.error;
  if (fallo.cuotaAnonimaAgotada === true)
    return idioma === 'es'
      ? 'Has agotado las preguntas sin cuenta de hoy. Regístrate para seguir preguntando.'
      : 'You have used today’s questions without an account. Register to keep asking.';
  if (fallo.tipo === 'cuota' && fallo.reintentarEn !== null) {
    return t.cuotaConEspera.replace('{segundos}', String(fallo.reintentarEn));
  }
  if (fallo.tipo === 'tope_de_gasto_anonimo') {
    const { antes, enlace, despues } = t.tope_de_gasto_anonimo;
    return `${antes}${enlace}${despues}`;
  }
  return t[fallo.tipo];
}
